import { useQuery } from '@tanstack/react-query';
import {
  listBalanceTypes,
  listCountries,
  listItemTypes,
  listLocationTypes,
  listStateProvinces,
  listUoms,
} from './api';

/**
 * Reference-data hooks. These are per-tenant constants that change rarely, so
 * they're cached for the session rather than refetched per screen — the item
 * form opens straight from cache after the first load.
 */

const REFERENCE_DATA_STALE_MS = 60 * 60 * 1000;

export const UOMS_KEY = ['lookups', 'uoms'] as const;
export const ITEM_TYPES_KEY = ['lookups', 'item-types'] as const;
export const LOCATION_TYPES_KEY = ['lookups', 'location-types'] as const;
export const BALANCE_TYPES_KEY = ['lookups', 'balance-types'] as const;
export const COUNTRIES_KEY = ['lookups', 'countries'] as const;

export function useUoms() {
  return useQuery({
    queryKey: UOMS_KEY,
    queryFn: ({ signal }) => listUoms(signal),
    staleTime: REFERENCE_DATA_STALE_MS,
  });
}

export function useItemTypes() {
  return useQuery({
    queryKey: ITEM_TYPES_KEY,
    queryFn: ({ signal }) => listItemTypes(signal),
    staleTime: REFERENCE_DATA_STALE_MS,
  });
}

export function useLocationTypes() {
  return useQuery({
    queryKey: LOCATION_TYPES_KEY,
    queryFn: ({ signal }) => listLocationTypes(signal),
    staleTime: REFERENCE_DATA_STALE_MS,
  });
}

export function useBalanceTypes() {
  return useQuery({
    queryKey: BALANCE_TYPES_KEY,
    queryFn: ({ signal }) => listBalanceTypes(signal),
    staleTime: REFERENCE_DATA_STALE_MS,
  });
}

export function useCountries() {
  return useQuery({
    queryKey: COUNTRIES_KEY,
    queryFn: ({ signal }) => listCountries(signal),
    staleTime: REFERENCE_DATA_STALE_MS,
  });
}

/**
 * The subdivisions of one country.
 *
 * Keyed by country and disabled without one, so switching the country picker fetches the right list
 * rather than filtering a stale one. A country with no subdivisions returns an empty list, which is
 * a real answer and not an error.
 */
export function useStateProvinces(countryCode: string | null) {
  return useQuery({
    queryKey: ['lookups', 'state-provinces', countryCode] as const,
    queryFn: ({ signal }) => listStateProvinces(countryCode!, signal),
    enabled: Boolean(countryCode),
    staleTime: REFERENCE_DATA_STALE_MS,
  });
}
